import TableHeader from '@src/common/TableHeader';
import { Box } from '@uhg-abyss/web/ui/Box';

export const reformColumns = (columns) => {
  return columns.map((column) => ({
    ...column,
    Header: column?.accessor ? (
      <TableHeader title={column?.accessor}>{column?.Header}</TableHeader>
    ) : (
      column?.Header
    ),
  }));
};

/**
 * Resolve a stable column id for an Abyss v1 column definition.
 */
const resolveColumnId = (column, index) => {
  if (column?.id) {
    return column.id;
  }
  if (typeof column?.accessor === 'string' && column.accessor !== '') {
    return column.accessor;
  }
  return `column-${index}`;
};

/**
 * Adapts v1 column definitions to the v2 `DataTableColumn` shape.
 *
 * Key mapping:
 * - `Header`             -> `header`
 * - `accessor` (string)  -> `accessorKey`
 * - `Cell`               -> `cell` (shims `{ row, value, data }` signature)
 * - `width`              -> `size`
 * - `minWidth`           -> `minSize`
 * - `canToggleVisibilty` -> `enableHiding`
 * - `isHiddenByDefault`  -> handled via `getInitialColumnVisibility`
 *
 * Hidden-by-default columns are dropped entirely — v2 reserves 8px of layout
 * space for them and this app has no column-visibility toggle UI.
 */
export const isPermanentlyHiddenColumn = (column: any) =>
  column?.isHiddenByDefault === true;

export const mapV1ColumnsToV2 = (columns: any[] = []) => {
  return columns
    .filter((column) => column != null && !isPermanentlyHiddenColumn(column))
    .map((column, index) => {
      const {
        Header,
        accessor,
        Cell,
        width,
        minWidth,
        canToggleVisibilty,
        isHiddenByDefault: _isHiddenByDefault,
        ...rest
      } = column || {};

      const v2Column: any = { ...rest };
      v2Column.id = resolveColumnId(column, index);

      if (typeof accessor === 'string' && accessor !== '') {
        v2Column.accessorKey = accessor;
      } else if (typeof accessor === 'function') {
        v2Column.accessorFn = accessor;
      }

      if (Header !== undefined) {
        v2Column.header = Header;
      }

      if (typeof Cell === 'function') {
        v2Column.cell = (context) =>
          Cell({
            ...context,
            value: context?.getValue?.(),
            data: context?.table?.options?.data ?? [],
          });
      }

      if (width != null) {
        v2Column.size = width;
      }
      if (minWidth != null) {
        v2Column.minSize = minWidth;
      }
      if (canToggleVisibilty !== undefined) {
        v2Column.enableHiding = canToggleVisibilty;
      }

      return v2Column;
    });
};

/**
 * Ensures every row has a stable `uniqueId` for the v2 `useDataTable` hook.
 * Rows that already have one are left untouched.
 */
export const withRowIds = <T extends Record<string, any>>(
  rows: T[] = []
): T[] => {
  if (!Array.isArray(rows)) {
    return rows;
  }
  return rows.map((row, index) => ({
    ...row,
    uniqueId: row?.uniqueId ?? index,
  }));
};

/** Builds initial TanStack `columnVisibility` from v1 `isHiddenByDefault`. */
export const getInitialColumnVisibility = (columns: any[] = []) => {
  const visibility: Record<string, boolean> = {};
  columns
    .filter((column) => column != null && !isPermanentlyHiddenColumn(column))
    .forEach((column, index) => {
      if (column?.isHiddenByDefault) {
        visibility[resolveColumnId(column, index)] = false;
      }
    });
  return visibility;
};

export const cellValueHighLight = ({
  value,
  fileData,
  typeValue,
  changeowner = '',
}) => {
  const isMulti = fileData?.multilevelSubmitter;
  const highlight = isMulti && typeValue?.trim().length > 0;

  let color = '';
  if (changeowner === 'CO') {
    color = highlight ? '#CEF571' : '#FAFAA7';
  } else {
    color = highlight ? '#DDF5C6' : '';
  }

  return <Box color={color}>{value}</Box>;
};
